import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;
      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}

// Safety net for video-background-component instances that missed connectedCallback
// (e.g. elements inside Swiper cloned slides)
function initVideoBackgrounds() {
  document.querySelectorAll('video-background-component').forEach((el) => {
    const sources = el.querySelectorAll('[data-video-source]');
    const video = el.querySelector('video');
    if (!video || video.dataset.initialized) return;

    for (const source of sources) {
      if (source.dataset.videoSource) {
        source.setAttribute('src', source.dataset.videoSource);
      }
    }

    video.dataset.initialized = 'true';
    video.load();
  });
}

document.addEventListener('DOMContentLoaded', initVideoBackgrounds);

// iOS autoplay fix — ensures muted + playsinline on all autoplay videos
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('video[autoplay]').forEach((video) => {
    video.muted = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');
    if (!video.closest('deferred-media[autoplay]')) {
      video.play().catch(() => {});
    }
  });
});

// Catch videos injected after page load (deferred-media, Swiper clones, etc.)
const videoAutoplayObserver = new MutationObserver(() => {
  document.querySelectorAll('video:not([data-ios-init])').forEach((video) => {
    video.setAttribute('data-ios-init', '');
    video.muted = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.removeAttribute('controls');
    if (!video.closest('deferred-media[autoplay]')) {
      video.play().catch(() => {});
    }
  });
});

videoAutoplayObserver.observe(document.body, { childList: true, subtree: true });

// Restart looping videos when they re-enter the viewport
const videoLoopObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      const video = entry.target;
      if (entry.isIntersecting) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  },
  { threshold: 0.1 }
);

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('video[autoplay]').forEach((video) => {
    if (!video.closest('deferred-media[autoplay]')) {
      videoLoopObserver.observe(video);
    }
  });
});

// Also catch videos added after page load
const videoLoopMutationObserver = new MutationObserver(() => {
  document.querySelectorAll('video[autoplay]:not([data-loop-observed])').forEach((video) => {
    video.setAttribute('data-loop-observed', '');
    if (!video.closest('deferred-media[autoplay]')) {
      videoLoopObserver.observe(video);
    }
  });
});

videoLoopMutationObserver.observe(document.body, { childList: true, subtree: true });
